import { useState, useMemo, useEffect, useCallback } from "react";
import {
  FaMoneyBillWave,
  FaTrashAlt,
  FaTimes,
  FaFileInvoiceDollar,
  FaUserCheck,
  FaCalendarAlt,
  FaEye,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./InstantLoanReport.css";
import { toast } from "react-toastify";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const InstantLoanReport = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Modals
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDisburseModalOpen, setIsDisburseModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingLoan, setViewingLoan] = useState(null);

  // Form States
  const [approveFormData, setApproveFormData] = useState({
    approvedAmount: "",
    interestRate: "18",
    tenure: "12",
    adminRemarks: "Loan approved",
  });

  const [disburseFormData, setDisburseFormData] = useState({
    disbursedAmount: "",
    disbursementDate: "",
    disbursementMode: "BANK_TRANSFER",
    transactionId: "",
    emiStartDate: "",
    firstEmiDate: "",
    adminRemarks: "Amount disbursed successfully",
  });

  // Action Loading States
  const [isFetching, setIsFetching] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDisbursing, setIsDisbursing] = useState(false);

  const superAdminId = localStorage.getItem("adminuserId") || undefined;
  const BASE_URL = "https://payyug.in/api";

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const generateTransactionId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `TXN${year}${month}${day}${random}`;
  };

  // Safe Currency Formatter (accepts string or number)
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "—";
    const num = Number(amount);
    if (isNaN(num)) return String(amount);

    return `₹${num.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  };

  // Fetch API Loans
  const fetchInstantLoans = useCallback(async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(`${BASE_URL}/instant-loan/applications`, {
        params: {
          page: currentPage,
          limit: rowsPerPage === -1 ? 100000 : rowsPerPage,
        },
      });

      const list = res.data?.data || res.data?.applications || res.data || [];
      setLoans(Array.isArray(list) ? list : []);
      setTotalPages(res.data?.totalPages || 1);
      setTotalRecords(res.data?.totalRecords || list.length || 0);
      setHasNextPage(
        Boolean(
          res.data?.hasNextPage || currentPage < (res.data?.totalPages || 1),
        ),
      );
      setHasPreviousPage(Boolean(res.data?.hasPreviousPage || currentPage > 1));
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch instant loans.",
      );
    } finally {
      setIsFetching(false);
    }
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    fetchInstantLoans();
  }, [fetchInstantLoans]);

  const handleApproveChange = (e) => {
    const { name, value } = e.target;
    setApproveFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDisburseChange = (e) => {
    const { name, value } = e.target;
    setDisburseFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Search Filter
  const filteredLoans = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return loans;

    return loans.filter((item) => {
      return (
        String(item.id || "")
          .toLowerCase()
          .includes(term) ||
        String(item.applicationNo || "")
          .toLowerCase()
          .includes(term) ||
        String(item.accountHolderName || item.applicantName || "")
          .toLowerCase()
          .includes(term) ||
        String(item.mobile || item.accountNumber || "").includes(term) ||
        String(item.panNumber || "")
          .toLowerCase()
          .includes(term) ||
        String(item.status || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [loans, searchTerm]);

  // Modal Handlers
  const handleOpenViewModal = (loan) => {
    setViewingLoan(loan);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewingLoan(null);
  };

  const handleViewEmiDetails = (loan) => {
    navigate(`/instant-loan/emi-details/${loan.id}`, {
      state: { loanData: loan },
    });
  };

  const handleOpenApproveModal = (loan) => {
    setSelectedLoan(loan);
    setApproveFormData({
      approvedAmount: loan.approvedAmount || loan.loanAmount || "",
      interestRate: loan.interestRate || "18",
      tenure: loan.tenure || loan.tenureMonths || "12",
      adminRemarks: loan.adminRemarks || "Loan approved",
    });
    setIsApproveModalOpen(true);
  };

  // Submit Approval
  const handleConfirmApprove = async (e) => {
    e.preventDefault();
    if (!selectedLoan) return;

    if (
      !approveFormData.approvedAmount ||
      Number(approveFormData.approvedAmount) <= 0
    ) {
      toast.error("Please enter a valid approved amount.");
      return;
    }
    if (
      !approveFormData.interestRate ||
      Number(approveFormData.interestRate) <= 0
    ) {
      toast.error("Please enter a valid interest rate.");
      return;
    }
    if (!approveFormData.tenure || Number(approveFormData.tenure) <= 0) {
      toast.error("Please enter a valid tenure.");
      return;
    }

    const payload = {
      approvedAmount: Number(approveFormData.approvedAmount),
      interestRate: Number(approveFormData.interestRate),
      tenure: Number(approveFormData.tenure),
      approvedBy: Number(superAdminId) || 1,
      adminRemarks: approveFormData.adminRemarks.trim() || "Loan approved",
    };

    try {
      setIsApproving(true);
      const res = await axios.put(
        `${BASE_URL}/instant-loan/application/${selectedLoan.id}/approve`,
        payload,
      );
      toast.success(res.data?.message || "Loan approved successfully.");
      setIsApproveModalOpen(false);
      setSelectedLoan(null);
      await fetchInstantLoans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve loan.");
    } finally {
      setIsApproving(false);
    }
  };

  // Open Disburse Modal
  const handleOpenDisburseModal = (loan) => {
    setSelectedLoan(loan);
    const today = new Date();
    const emiDate = new Date(today);
    emiDate.setMonth(emiDate.getMonth() + 1);

    const formattedToday = formatDate(today);
    const formattedEmiDate = formatDate(emiDate);

    setDisburseFormData({
      disbursedAmount: loan.approvedAmount || loan.loanAmount || "",
      disbursementDate: formattedToday,
      disbursementMode: "BANK_TRANSFER",
      transactionId: generateTransactionId(),
      emiStartDate: formattedEmiDate,
      firstEmiDate: formattedEmiDate,
      adminRemarks: "Amount disbursed successfully",
    });
    setIsDisburseModalOpen(true);
  };

  // Submit Disbursement
  const handleConfirmDisburse = async (e) => {
    e.preventDefault();
    if (!selectedLoan) return;

    if (
      !disburseFormData.disbursedAmount ||
      Number(disburseFormData.disbursedAmount) <= 0
    ) {
      toast.error("Please enter a valid disbursed amount.");
      return;
    }
    if (!disburseFormData.disbursementDate) {
      toast.error("Please select a disbursement date.");
      return;
    }

    const payload = {
      disbursedAmount: Number(disburseFormData.disbursedAmount),
      disbursementDate: disburseFormData.disbursementDate,
      disbursementMode: disburseFormData.disbursementMode,
      transactionId: disburseFormData.transactionId || generateTransactionId(),
      emiStartDate: disburseFormData.emiStartDate,
      firstEmiDate: disburseFormData.firstEmiDate,
      adminRemarks:
        disburseFormData.adminRemarks.trim() || "Amount disbursed successfully",
    };

    try {
      setIsDisbursing(true);
      const res = await axios.post(
        `${BASE_URL}/instant-loan/application/${selectedLoan.id}/disburse`,
        payload,
      );
      toast.success(res.data?.message || "Loan disbursed successfully.");
      setIsDisburseModalOpen(false);
      setSelectedLoan(null);
      await fetchInstantLoans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to disburse loan.");
    } finally {
      setIsDisbursing(false);
    }
  };

  // Delete Record
  const handleDeleteLoan = async (loanId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this loan application record?",
      )
    )
      return;
    try {
     const res=await axios.delete(`${BASE_URL}/instant-loan/application//${loanId}`);
      toast.success(res?.data?.message||"Loan application deleted successfully.");
      await fetchInstantLoans();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete loan application.",
      );
    }
  };

  return (
    <div className="ilr-main-container">
      {/* Header */}
      <div className="ilr-page-header">
        <div className="ilr-header-info">
          <h2>
            <FaFileInvoiceDollar className="ilr-title-icon" />
            Instant Loan Report
          </h2>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="ilr-card">
        {/* Toolbar */}
        <div className="ilr-toolbar">
          <div className="ilr-search-box">
            <input
              type="text"
              placeholder="Search by application no, name, bank, PAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ilr-search-input"
            />
          </div>

          <div className="ilr-rows-selector">
            <label htmlFor="ilr-rows-select">Show:</label>
            <select
              id="ilr-rows-select"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="ilr-select"
            >
              <option value={10}>10 Rows</option>
              <option value={25}>25 Rows</option>
              <option value={50}>50 Rows</option>
              <option value={100}>100 Rows</option>
              <option value={-1}>All</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="ilr-table-responsive">
          <table className="ilr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Application No.</th>
                <th>Applicant Details</th>
                <th>Bank & PAN</th>
                <th>Applied Amt</th>
                <th>Approved Amt</th>
                <th>Tenure</th>
                <th>Status</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isFetching ? (
                <tr>
                  <td colSpan={9} className="ilr-table-empty">
                    Loading loan applications...
                  </td>
                </tr>
              ) : filteredLoans.length > 0 ? (
                filteredLoans.map((row, index) => {
                  const statusKey = String(row.status || "").toUpperCase();
                  const isPending =
                    statusKey === "PENDING" || statusKey === "APPLIED";
                  const isApproved = statusKey === "APPROVED";
                  const isDisbursed = statusKey === "DISBURSED";

                  return (
                    <tr key={row.id}>
                      <td>
                        {(currentPage - 1) *
                          (rowsPerPage === -1 ? totalRecords : rowsPerPage) +
                          index +
                          1}
                      </td>
                      <td>
                        <span className="ilr-badge-id">
                          {row.applicationNo || `APP-${row.id}`}
                        </span>
                      </td>
                      <td>
                        <div className="ilr-applicant-cell">
                          <strong>
                            {row.accountHolderName ||
                              row.applicantName ||
                              "N/A"}
                          </strong>
                          <span>Score: {row.cibilScore || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <div className="ilr-applicant-cell">
                          <span className="ilr-mono">
                            {row.panNumber || "—"}
                          </span>
                          <small style={{ color: "#64748b" }}>
                            {row.bankName || "—"}
                          </small>
                        </div>
                      </td>
                      <td className="ilr-amount">
                        {formatCurrency(row.loanAmount || row.appliedAmount)}
                      </td>
                      <td className="ilr-amount">
                        {row.approvedAmount
                          ? formatCurrency(row.approvedAmount)
                          : "—"}
                      </td>
                      <td>
                        {row.tenure || 0} {row.tenureType || "M"}
                      </td>
                      <td>
                        <span
                          className={`ilr-status-pill ilr-status-${statusKey.toLowerCase()}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <div className="ilr-action-btn-group">
                          <button
                            type="button"
                            className="ilr-btn-action ilr-btn-view"
                            title="View Full Details"
                            onClick={() => handleOpenViewModal(row)}
                          >
                            <FaEye />
                            <span>View</span>
                          </button>

                          {isPending && (
                            <button
                              type="button"
                              className="ilr-btn-action ilr-btn-approve"
                              title="Approve Loan"
                              onClick={() => handleOpenApproveModal(row)}
                            >
                              <FaUserCheck />
                              <span>Approve</span>
                            </button>
                          )}

                          {isApproved && (
                            <button
                              type="button"
                              className="ilr-btn-action ilr-btn-disburse"
                              title="Disburse Loan"
                              onClick={() => handleOpenDisburseModal(row)}
                            >
                              <FaMoneyBillWave />
                              <span>Disburse</span>
                            </button>
                          )}

                          {/* {isDisbursed && (
                            <span className="ilr-disbursed-tag">
                              <FaCheckCircle /> Disbursed
                            </span>
                          )} */}
                          {isDisbursed && (
                            <button
                              type="button"
                              className="ilr-btn-action ilr-btn-emi"
                              title="View EMI Schedule"
                              onClick={() => handleViewEmiDetails(row)}
                            >
                              <FaCalendarAlt />
                              <span>EMI Details</span>
                            </button>
                          )}

                          <button
                            type="button"
                            className="ilr-btn-action ilr-btn-delete"
                            title="Delete Application"
                            onClick={() => handleDeleteLoan(row.id)}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="ilr-table-empty">
                    No loan applications match your query.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="ilr-pagination-wrapper">
          <div className="ilr-pagination-summary">
            {totalRecords === 0 ? (
              "Showing 0 of 0 entries"
            ) : (
              <>
                Showing{" "}
                {(currentPage - 1) *
                  (rowsPerPage === -1 ? totalRecords : rowsPerPage) +
                  1}{" "}
                to{" "}
                {rowsPerPage === -1
                  ? totalRecords
                  : Math.min(currentPage * rowsPerPage, totalRecords)}{" "}
                of {totalRecords} entries
              </>
            )}
          </div>

          <div className="ilr-pagination-nav">
            <button
              type="button"
              className="ilr-nav-btn"
              disabled={!hasPreviousPage || isFetching || currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <ChevronLeft size={16} /> Prev
            </button>

            <span className="ilr-page-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="ilr-nav-btn"
              disabled={!hasNextPage || isFetching || currentPage >= totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* VIEW MODAL */}
      {isViewModalOpen && viewingLoan && (
        <div className="ilr-modal-backdrop" onClick={closeViewModal}>
          <div
            className="ilr-modal-box ilr-view-modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ilr-modal-head">
              <h3>Loan Application Details</h3>
              <button
                type="button"
                className="ilr-modal-close-btn"
                onClick={closeViewModal}
              >
                <FaTimes />
              </button>
            </div>

            <div className="ilr-modal-body ilr-view-modal-body">
              {/* Application Overview */}
              <div className="ilr-view-section">
                <h4>Application Overview</h4>
                <div className="ilr-view-grid">
                  <div className="ilr-view-item">
                    <label>Application No</label>
                    <p className="ilr-mono">
                      {viewingLoan.applicationNo || `APP-${viewingLoan.id}`}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Status</label>
                    <p>
                      <span
                        className={`ilr-status-pill ilr-status-${String(viewingLoan.status || "").toLowerCase()}`}
                      >
                        {viewingLoan.status}
                      </span>
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Applied Date</label>
                    <p>
                      {viewingLoan.appliedAt
                        ? new Date(viewingLoan.appliedAt).toLocaleString(
                            "en-IN",
                          )
                        : "N/A"}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Applicant Name</label>
                    <p>
                      <strong>
                        {viewingLoan.accountHolderName ||
                          viewingLoan.applicantName ||
                          "N/A"}
                      </strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Loan Financials */}
              <div className="ilr-view-section">
                <h4>Loan & Tenor Details</h4>
                <div className="ilr-view-grid">
                  <div className="ilr-view-item">
                    <label>Requested Amount</label>
                    <p className="ilr-highlight-text">
                      {formatCurrency(viewingLoan.loanAmount)}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Approved Amount</label>
                    <p className="ilr-highlight-text">
                      {viewingLoan.approvedAmount
                        ? formatCurrency(viewingLoan.approvedAmount)
                        : "Not Sanctioned"}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Interest Rate</label>
                    <p>
                      {viewingLoan.interestRate
                        ? `${viewingLoan.interestRate}% (${viewingLoan.interestType || "Fixed"})`
                        : "—"}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Tenure</label>
                    <p>
                      {viewingLoan.tenure || "—"}{" "}
                      {viewingLoan.tenureType || "MONTH"}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>EMI Amount</label>
                    <p>{formatCurrency(viewingLoan.emiAmount)}</p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Net Disbursal</label>
                    <p>{formatCurrency(viewingLoan.disbursedAmount)}</p>
                  </div>
                </div>
              </div>

              {/* Banking & Identity */}
              <div className="ilr-view-section">
                <h4>Banking & Identity</h4>
                <div className="ilr-view-grid">
                  <div className="ilr-view-item">
                    <label>Bank Name</label>
                    <p>{viewingLoan.bankName || "—"}</p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Account Number</label>
                    <p className="ilr-mono">
                      {viewingLoan.accountNumber || "—"}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>IFSC Code</label>
                    <p className="ilr-mono">{viewingLoan.ifscCode || "—"}</p>
                  </div>
                  <div className="ilr-view-item">
                    <label>PAN Number</label>
                    <p className="ilr-mono">{viewingLoan.panNumber || "—"}</p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Identity / Aadhaar</label>
                    <p className="ilr-mono">
                      {viewingLoan.aadhaarNumber
                        ? viewingLoan.aadhaarNumber
                        : "—"}
                    </p>
                  </div>
                  <div className="ilr-view-item">
                    <label>CIBIL Score</label>
                    <p>{viewingLoan.cibilScore || "—"}</p>
                  </div>
                  <div className="ilr-view-item">
                    <label>Transaction ID</label>
                    <p className="ilr-mono">
                      {viewingLoan.transactionId || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="ilr-view-section">
                <h4>Remarks</h4>
                <div className="ilr-view-notes">
                  <p>
                    <strong>Customer Note:</strong>{" "}
                    {viewingLoan.customerRemarks || "No remarks provided"}
                  </p>
                  <p>
                    <strong>Admin Note:</strong>{" "}
                    {viewingLoan.adminRemarks || "No remarks recorded"}
                  </p>
                </div>
              </div>
            </div>

            <div className="ilr-modal-foot">
              <button
                type="button"
                className="ilr-btn-secondary"
                onClick={closeViewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APPROVAL MODAL */}
      {isApproveModalOpen && selectedLoan && (
        <div
          className="ilr-modal-backdrop"
          onClick={() => !isApproving && setIsApproveModalOpen(false)}
        >
          <div className="ilr-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="ilr-modal-head">
              <h3>Approve Loan Application</h3>
              <button
                type="button"
                className="ilr-modal-close-btn"
                disabled={isApproving}
                onClick={() => setIsApproveModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleConfirmApprove}>
              <div className="ilr-modal-body">
                <div className="ilr-summary-box">
                  <div>
                    <label>Applicant Name</label>
                    <p>
                      {selectedLoan.accountHolderName ||
                        selectedLoan.applicantName ||
                        "N/A"}
                    </p>
                  </div>
                  <div>
                    <label>Requested Amount</label>
                    <p className="highlight">
                      {formatCurrency(selectedLoan.loanAmount)}
                    </p>
                  </div>
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="approvedAmount">
                    Sanctioned / Approved Amount (₹)
                  </label>
                  <input
                    id="approvedAmount"
                    type="number"
                    name="approvedAmount"
                    value={approveFormData.approvedAmount}
                    onChange={handleApproveChange}
                    className="ilr-input"
                    min="1"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="interestRate">Interest Rate (%)</label>
                  <input
                    id="interestRate"
                    type="number"
                    step="0.01"
                    name="interestRate"
                    value={approveFormData.interestRate}
                    onChange={handleApproveChange}
                    className="ilr-input"
                    min="0"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="tenure">Tenure (Months)</label>
                  <input
                    id="tenure"
                    type="number"
                    name="tenure"
                    value={approveFormData.tenure}
                    onChange={handleApproveChange}
                    className="ilr-input"
                    min="1"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="approveRemarks">Admin Remarks</label>
                  <textarea
                    id="approveRemarks"
                    name="adminRemarks"
                    value={approveFormData.adminRemarks}
                    onChange={handleApproveChange}
                    className="ilr-input"
                    rows={2}
                  />
                </div>
              </div>

              <div className="ilr-modal-foot">
                <button
                  type="button"
                  className="ilr-btn-secondary"
                  disabled={isApproving}
                  onClick={() => setIsApproveModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ilr-btn-primary"
                  disabled={isApproving}
                >
                  {isApproving ? "Approving..." : "Approve Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DISBURSAL MODAL */}
      {isDisburseModalOpen && selectedLoan && (
        <div
          className="ilr-modal-backdrop"
          onClick={() => !isDisbursing && setIsDisburseModalOpen(false)}
        >
          <div className="ilr-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="ilr-modal-head">
              <h3>Confirm Loan Disbursement</h3>
              <button
                type="button"
                className="ilr-modal-close-btn"
                disabled={isDisbursing}
                onClick={() => setIsDisburseModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleConfirmDisburse}>
              <div className="ilr-modal-body">
                <div className="ilr-summary-box">
                  <div>
                    <label>Applicant Name</label>
                    <p>
                      {selectedLoan.accountHolderName ||
                        selectedLoan.applicantName ||
                        "N/A"}
                    </p>
                  </div>
                  <div>
                    <label>Bank Account</label>
                    <p className="highlight">
                      {selectedLoan.bankName || "Bank"} -{" "}
                      {selectedLoan.accountNumber || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="disbursedAmount">Disbursed Amount (₹)</label>
                  <input
                    id="disbursedAmount"
                    type="number"
                    name="disbursedAmount"
                    value={disburseFormData.disbursedAmount}
                    onChange={handleDisburseChange}
                    className="ilr-input"
                    min="1"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="disbursementDate">Disbursement Date</label>
                  <input
                    id="disbursementDate"
                    type="date"
                    name="disbursementDate"
                    value={disburseFormData.disbursementDate}
                    onChange={handleDisburseChange}
                    className="ilr-input"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="disbursementMode">Disbursement Mode</label>
                  <select
                    id="disbursementMode"
                    name="disbursementMode"
                    value={disburseFormData.disbursementMode}
                    onChange={handleDisburseChange}
                    className="ilr-input"
                  >
                    <option value="BANK_TRANSFER">BANK TRANSFER</option>
                    <option value="NEFT">NEFT</option>
                    <option value="RTGS">RTGS</option>
                    <option value="IMPS">IMPS</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="transactionId">
                    Transaction Reference ID
                  </label>
                  <input
                    id="transactionId"
                    type="text"
                    name="transactionId"
                    value={disburseFormData.transactionId}
                    onChange={handleDisburseChange}
                    className="ilr-input"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="firstEmiDate">First EMI Date</label>
                  <input
                    id="firstEmiDate"
                    type="date"
                    name="firstEmiDate"
                    value={disburseFormData.firstEmiDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDisburseFormData((prev) => ({
                        ...prev,
                        firstEmiDate: val,
                        emiStartDate: val,
                      }));
                    }}
                    className="ilr-input"
                    required
                  />
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="disburseRemarks">Admin Remarks</label>
                  <textarea
                    id="disburseRemarks"
                    name="adminRemarks"
                    value={disburseFormData.adminRemarks}
                    onChange={handleDisburseChange}
                    className="ilr-input"
                    rows={2}
                  />
                </div>
              </div>

              <div className="ilr-modal-foot">
                <button
                  type="button"
                  className="ilr-btn-secondary"
                  disabled={isDisbursing}
                  onClick={() => setIsDisburseModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ilr-btn-disburse-confirm"
                  disabled={isDisbursing}
                >
                  {isDisbursing ? "Disbursing..." : "Confirm Disbursement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantLoanReport;
